import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import '../App.css';

import { API_BASE_URL, getToken, formatDate } from '../dppApi';
import { useLocalLang, LANG_LABELS } from '../hooks/useLocalLang';

// Public Digital Product Passport page. This is where a scanned product QR code
// lands (QR -> /api/passport/<id>/r -> 302 -> /jewellery/<id>).

// Product-specific field values to translate — UI labels/section titles stay in
// English and are not sent to /translate-batch.
function collectDynamicTexts(passport) {
    const materials = passport?.materials || {};
    const eu = passport?.eu_compliance || {};
    const texts = [materials.metal_type, materials.metal_purity, eu.country_of_origin, eu.care_info];
    (materials.gemstones || []).forEach(g => {
        texts.push(g.stone_type, g.origin, g.cut, g.color, g.clarity);
    });
    return texts.filter(s => typeof s === 'string' && s.length > 0);
}

const Passport = () => {
    const { qrCodeId } = useParams();
    const [passport, setPassport] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showLocal, setShowLocal] = useState(false);
    const [localContent, setLocalContent] = useState(null);
    const [uiTranslations, setUiTranslations] = useState({}); // { [lang]: { [originalText]: translatedText } }
    const localLang = useLocalLang(); // resolved from viewer's GPS/IP location
    const [logoFailed, setLogoFailed] = useState(false);
    const [headerLogoFailed, setHeaderLogoFailed] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    const [isOwner, setIsOwner] = useState(false);
    const [isBrand, setIsBrand] = useState(false);
    const [privateDocs, setPrivateDocs] = useState([]);
    const [ownershipHistory, setOwnershipHistory] = useState([]);
    const [repairs, setRepairs] = useState([]);            // from /repairs (login-gated, tenure-bounded)
    const [canViewRepairs, setCanViewRepairs] = useState(false);  // /repairs returned 200 (current OR previous owner, or brand)
    const [canViewPrivateDocs, setCanViewPrivateDocs] = useState(false);  // /private-docs returned 200 (current OR previous owner)
    const slidesRef = useRef(null);

    useEffect(() => {
        // This component instance is reused across products (same /jewellery/:qrCodeId
        // route, different param), so clear every viewer-scoped piece of state on each
        // load. Otherwise a previously-viewed passport's ownership history or private
        // docs could render on a passport the viewer isn't authorized for. Each field
        // is then repopulated only by THIS product's authorized fetches below.
        setIsOwner(false);
        setIsBrand(false);
        setPrivateDocs([]);
        setOwnershipHistory([]);
        setRepairs([]);
        setCanViewRepairs(false);
        setCanViewPrivateDocs(false);
        setError(null);
        // Forward ?preview=1 from the page URL to the view-recording endpoint so
        // brand/demo opens are tagged is_test=true and excluded from analytics.
        // Only this param is forwarded; nothing else about the request changes.
        const previewQS = new URLSearchParams(window.location.search).get("preview") === "1" ? "?preview=1" : "";
        fetch(`${API_BASE_URL}/api/passport/${qrCodeId}${previewQS}`)
            .then(res => {
                if (!res.ok) throw new Error(res.status === 404 ? "Passport not found" : "Failed to load passport");
                return res.json();
            })
            .then(data => { setPassport(data); setLocalContent(null); setShowLocal(false); setLogoFailed(false); setHeaderLogoFailed(false); })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [qrCodeId]);

    useEffect(() => {
        // Role source (Option B): when the passport is opened AS the brand
        // (?as=brand — the brand dashboard/maintenance links carry it), skip every
        // user-token fetch so owner-only UI (Contact Merchant, "Me", Estate Planning)
        // never renders even if a user is ALSO logged in in this browser. The brand's
        // own doc/repair/ownership visibility comes from the brand effect below and is
        // unchanged. This only gates client-side rendering — backend authorization
        // (see test_doc_access) is untouched.
        if (new URLSearchParams(window.location.search).get('as') === 'brand') return;
        const token = getToken('user');
        if (!token || !qrCodeId) return;
        fetch(`${API_BASE_URL}/api/passport/${qrCodeId}/ownership`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => res.ok ? res.json() : { is_owner: false })
            .then(data => {
                setIsOwner(!!data.is_owner);
                setOwnershipHistory(data.ownership_history || []);
            })
            .catch(() => {});
        // Private docs are login-gated and tenure-bounded like repairs: the backend
        // returns 200 for the current OR any previous owner (their receipts/appraisals
        // up to the extent of their ownership; only their own insurance) and 403 for a
        // never-owner. Fetch unconditionally and let the endpoint decide.
        fetch(`${API_BASE_URL}/api/passport/${qrCodeId}/private-docs`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => { setCanViewPrivateDocs(res.ok); return res.ok ? res.json() : []; })
            .then(setPrivateDocs)
            .catch(() => {});
        // Repairs are login-gated and tenure-bounded (see test_doc_access): the
        // backend returns 200 for the current OR any *previous* owner (bounded to
        // the extent of their ownership) and 403 for anyone who never owned it.
        // Fetch unconditionally and let the endpoint's authorization decide, so a
        // previous owner still sees the repairs from a passport they used to own.
        fetch(`${API_BASE_URL}/api/passport/${qrCodeId}/repairs`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => { setCanViewRepairs(res.ok); return res.ok ? res.json() : []; })
            .then(setRepairs)
            .catch(() => {});
    }, [qrCodeId]);

    useEffect(() => {
        const token = getToken('brand');
        if (!token || !qrCodeId) return;
        // Treat the viewer as "this product's brand" only if the backend
        // authorizes them for it. /private-docs returns 200 for the
        // manufacturing brand and 403 for any other brand, so the response
        // status — not mere token presence — decides isBrand. This keeps a
        // logged-in brand from getting brand-only UI on another brand's passport.
        fetch(`${API_BASE_URL}/api/passport/${qrCodeId}/private-docs`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(res => {
                if (!res.ok) { setIsBrand(false); return []; }   // 403 => not this product's brand
                setIsBrand(true);
                // Authorized for this product — also load the ownership history.
                // Brands may see who owns their pieces (owner identity included).
                fetch(`${API_BASE_URL}/api/passport/${qrCodeId}/ownership`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
                    .then(r => (r.ok ? r.json() : { ownership_history: [] }))
                    .then(d => setOwnershipHistory(d.ownership_history || []))
                    .catch(() => {});
                // Brand sees the full repair chain — login-gated /repairs.
                fetch(`${API_BASE_URL}/api/passport/${qrCodeId}/repairs`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
                    .then(r => { setCanViewRepairs(r.ok); return r.ok ? r.json() : []; })
                    .then(setRepairs)
                    .catch(() => {});
                return res.json();
            })
            .then(setPrivateDocs)
            .catch(() => { setIsBrand(false); });
    }, [qrCodeId]);

    useEffect(() => {
        const fontUrl = passport?.brand_font_url;
        if (!fontUrl) return;
        const isCdn = fontUrl.includes('fonts.googleapis.com') || fontUrl.includes('use.typekit.net');
        let href;
        if (isCdn) {
            href = fontUrl;
        } else {
            const params = new URLSearchParams({ url: fontUrl });
            if (passport.brand_font) params.set('family', passport.brand_font);
            href = `${API_BASE_URL}/api/font-proxy?${params}`;
        }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
        return () => { if (document.head.contains(link)) document.head.removeChild(link); };
    }, [passport?.brand_font_url]);

    const productLang = passport?.detected_lang || 'en';
    // Show the toggle when either the viewer's regional language or the product's own
    // language is non-English. Viewer's location takes priority.
    const displayLang = localLang || (productLang !== 'en' ? productLang : null);

    useEffect(() => {
        if (!showLocal || !displayLang || localContent) return;
        // Product is already in the target language — use stored text directly.
        if (displayLang === productLang && productLang !== 'en') {
            setLocalContent({ name: passport.name, description: passport.description });
            return;
        }
        // Otherwise request a translation from the backend.
        fetch(`${API_BASE_URL}/api/passport/${qrCodeId}/translate?lang=${displayLang}`)
            .then(r => r.json())
            .then(d => setLocalContent({ name: d.name, description: d.description }))
            .catch(() => setLocalContent({ name: passport.name_en || passport.name, description: passport.description_en || passport.description }));
    }, [showLocal, displayLang, localContent, qrCodeId, passport, productLang]);

    // Translate slides 2-3's product-specific field values (materials, gemstones,
    // compliance/care info) in one batched call. UI labels stay in English.
    useEffect(() => {
        if (!showLocal || !displayLang || !passport || uiTranslations[displayLang]) return;
        const texts = Array.from(new Set(collectDynamicTexts(passport)));
        if (!texts.length) return;
        fetch(`${API_BASE_URL}/api/passport/translate-batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ lang: displayLang, texts }),
        })
            .then(r => r.json())
            .then(d => {
                if (!Array.isArray(d.translations) || d.translations.length !== texts.length) return;
                const map = {};
                texts.forEach((orig, i) => { map[orig] = d.translations[i]; });
                setUiTranslations(prev => ({ ...prev, [displayLang]: map }));
            })
            .catch(() => {});
    }, [showLocal, displayLang, passport, uiTranslations]);

    const scrollToSlide = (i) => {
        const el = slidesRef.current;
        if (el) el.scrollTo({ left: i * el.offsetWidth, behavior: 'smooth' });
    };

    const onSlidesScroll = () => {
        const el = slidesRef.current;
        if (el) setActiveSlide(Math.round(el.scrollLeft / el.offsetWidth));
    };

    if (loading) return <div className="passport-page"><p className="passport-status">Loading passport…</p></div>;
    if (error) return <div className="passport-page"><p className="passport-status">{error}</p></div>;

    const { materials = {}, eu_compliance = {}, documents = [] } = passport;
    const receipts   = privateDocs.filter(d => d.doc_type === 'receipt');
    const appraisals = privateDocs.filter(d => d.doc_type === 'appraisal');
    const insurance  = privateDocs.filter(d => d.doc_type === 'insurance');
    const warranties = documents.filter(d => d.doc_type === 'warranty');

    const showImage = passport.image_url && /^https?:\/\//.test(passport.image_url);
    const localLabel = LANG_LABELS[displayLang] || displayLang?.toUpperCase();
    const displayName = showLocal ? (localContent?.name ?? passport.name) : (passport.name_en || passport.name);
    const displayDesc = showLocal ? (localContent?.description ?? passport.description) : (passport.description_en || passport.description);
    // Looks up the batch translation for a static label or dynamic field value;
    // falls back to the original English string when untranslated or not toggled on.
    const currentUiT = uiTranslations[displayLang] || {};
    const t = (s) => (typeof s === 'string' && s && showLocal && currentUiT[s]) || s;

    return (
        <div className="passport-page">
            <div className="passport-card">
                {displayLang && (
                    <div className="passport-lang-toggle">
                        <button
                            className={`lang-btn${!showLocal ? " active" : ""}`}
                            onClick={() => setShowLocal(false)}
                        >EN</button>
                        <button
                            className={`lang-btn${showLocal ? " active" : ""}`}
                            onClick={() => setShowLocal(true)}
                        >{localLabel}</button>
                    </div>
                )}
                <div className="passport-brand-header">
                    {passport.brand_header_logo_url && !headerLogoFailed ? (
                        passport.brand_header_logo_bg_color ? (
                            <div
                                className="passport-brand-header-logo-wrap"
                                style={{ backgroundColor: passport.brand_header_logo_bg_color }}
                            >
                                <img
                                    className="passport-brand-header-logo"
                                    src={passport.brand_header_logo_url}
                                    alt={passport.brand_title || passport.brand_name}
                                    onError={() => setHeaderLogoFailed(true)}
                                />
                            </div>
                        ) : (
                            <img
                                className="passport-brand-header-logo"
                                src={passport.brand_header_logo_url}
                                alt={passport.brand_title || passport.brand_name}
                                onError={() => setHeaderLogoFailed(true)}
                            />
                        )
                    ) : (
                        <span
                            style={{
                                color: passport.brand_text_color || passport.brand_color || undefined,
                                fontFamily: passport.brand_font ? `'${passport.brand_font}', sans-serif` : undefined,
                            }}
                        >
                            {passport.brand_title || passport.brand_name}
                        </span>
                    )}
                </div>

                <div className="passport-slides" ref={slidesRef} onScroll={onSlidesScroll}>
                    {/* Slide 1: Image + Description */}
                    <div className="passport-slide">
                        {showImage && <img className="passport-image" src={passport.image_url} alt={displayName} />}
                        <h1 className="passport-product-name">{displayName}</h1>
                        {displayDesc && (
                            <Section title="Description">
                                <p className="passport-description">{displayDesc}</p>
                            </Section>
                        )}
                    </div>

                    {/* Slide 2: Materials + Gemstones */}
                    <div className="passport-slide">
                        <Section title="Materials">
                            <Row label="Metal" value={t(materials.metal_type)} />
                            <Row label="Purity" value={t(materials.metal_purity)} />
                            <Row label="Weight (g)" value={materials.metal_weight_g} />
                        </Section>
                        {materials.gemstones && materials.gemstones.length > 0 && (
                            <Section title="Gemstones">
                                {materials.gemstones.map((g, i) => (
                                    <div className="passport-subblock" key={i}>
                                        <Row label="Type" value={t(g.stone_type)} />
                                        <Row label="Origin" value={t(g.origin)} />
                                        <Row label="Carat" value={g.carat} />
                                        <Row label="Cut" value={t(g.cut)} />
                                        <Row label="Color" value={t(g.color)} />
                                        <Row label="Clarity" value={t(g.clarity)} />
                                    </div>
                                ))}
                            </Section>
                        )}
                        <Section title="Certifications">
                            {materials.gemstones && materials.gemstones.some(g => g.certification_body)
                                ? materials.gemstones.filter(g => g.certification_body).map((g, i) => (
                                    <div className="passport-subblock" key={i}>
                                        <Row label="Stone" value={t(g.stone_type)} />
                                        <Row label="Body" value={g.certification_body} />
                                        <Row label="ID" value={g.certification_id} />
                                    </div>
                                ))
                                : null
                            }
                            {documents.filter(d => d.doc_type === 'certificate').map((d, i) => (
                                <div className="passport-subblock" key={`doc-${i}`}>
                                    <a
                                        href={d.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ fontSize: 14, color: '#555' }}
                                    >
                                        {d.issued_by ? `Certificate — ${d.issued_by}` : 'Certificate'}
                                        {d.issued_date ? ` (${formatDate(d.issued_date)})` : ''}
                                    </a>
                                </div>
                            ))}
                        </Section>
                        <Section title="Warranty">
                            {warranties.map((d, i) => (
                                <div className="passport-subblock" key={`warranty-${i}`}>
                                    <a
                                        href={d.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ fontSize: 14, color: '#555' }}
                                    >
                                        {d.issued_by ? `Warranty — ${d.issued_by}` : 'Warranty'}
                                        {d.issued_date ? ` (${formatDate(d.issued_date)})` : ''}
                                    </a>
                                </div>
                            ))}
                        </Section>
                        {(canViewPrivateDocs || (isBrand && receipts.length > 0)) && (
                            <Section title="Receipts">
                                {receipts.length === 0
                                    ? <p className="passport-description" style={{ color: '#bbb' }}>No receipts uploaded.</p>
                                    : receipts.map((r, i) => (
                                        <div className="passport-subblock" key={i}>
                                            <a href={r.url} target="_blank" rel="noreferrer" style={{ fontSize: 14, color: '#555' }}>
                                                {r.issued_by ? `Receipt — ${r.issued_by}` : 'Receipt'}
                                                {r.issued_date ? ` (${formatDate(r.issued_date)})` : ''}
                                            </a>
                                        </div>
                                    ))
                                }
                            </Section>
                        )}
                        {(canViewPrivateDocs || (isBrand && appraisals.length > 0)) && (
                            <Section title="Appraisals">
                                {appraisals.length === 0
                                    ? <p className="passport-description" style={{ color: '#bbb' }}>No appraisals uploaded.</p>
                                    : appraisals.map((d, i) => (
                                        <div className="passport-subblock" key={i}>
                                            <a href={d.url} target="_blank" rel="noreferrer" style={{ fontSize: 14, color: '#555' }}>
                                                {d.issued_by ? `Appraisal — ${d.issued_by}` : 'Appraisal'}
                                                {d.issued_date ? ` (${formatDate(d.issued_date)})` : ''}
                                            </a>
                                        </div>
                                    ))
                                }
                            </Section>
                        )}
                        {canViewPrivateDocs && (
                            <Section title="Insurance">
                                {insurance.length === 0
                                    ? <p className="passport-description" style={{ color: '#bbb' }}>No insurance uploaded.</p>
                                    : insurance.map((d, i) => (
                                        <div className="passport-subblock" key={i}>
                                            <a href={d.url} target="_blank" rel="noreferrer" style={{ fontSize: 14, color: '#555' }}>
                                                {d.issued_by ? `Insurance — ${d.issued_by}` : 'Insurance'}
                                                {d.issued_date ? ` (${formatDate(d.issued_date)})` : ''}
                                            </a>
                                        </div>
                                    ))
                                }
                            </Section>
                        )}
                    </div>

                    {/* Slide 3: Compliance / Mine / Journey / Care */}
                    <div className="passport-slide">
                        <Section title="Compliance">
                            <RowAll label="Country of origin" value={t(eu_compliance.country_of_origin)} />
                            <RowAll label="Carbon footprint" value={eu_compliance.carbon_footprint_kg} />
                            <RowAll label="RJC certified" value={fmtBool(eu_compliance.rjc_certified)} />
                        </Section>

                        <Section title="Care Information">
                            <p className="passport-description">{t(eu_compliance.care_info) || ""}</p>
                        </Section>
                        {(isOwner || ((canViewRepairs || isBrand) && repairs.length > 0)) && (
                            <Section title="Maintenance">
                                {/* Repair/service history — owner- and brand-only, never public.
                                    Any owner (current OR previous) sees it bounded to the extent of
                                    their tenure; the brand sees the full chain. Served from the
                                    authenticated /repairs endpoint (see test_doc_access). The
                                    request form below is current-owner-only. */}
                                {repairs.map((r, i) => (
                                    <div className="passport-subblock" key={`repair-${i}`}>
                                        <span style={{ fontSize: 14, color: '#555' }}>
                                            {r.description}{r.performed_by ? ` — ${r.performed_by}` : ''}{r.performed_at ? ` (${formatDate(r.performed_at)})` : ''}
                                        </span>
                                    </div>
                                ))}
                                {isOwner && passport.brand_contact_email && (
                                    <a
                                        className="passport-contact-btn"
                                        href={`mailto:${passport.brand_contact_email}?subject=${encodeURIComponent(`Maintenance request — ${passport.name || 'my piece'}`)}`}
                                    >
                                        Contact Merchant
                                    </a>
                                )}
                            </Section>
                        )}
                    </div>

                    {/* Slide 4: Ownership */}
                    <div className="passport-slide">
                        <Section title="Ownership">
                            <Row
                                label="Owner"
                                value={(() => {
                                    // Brands (only on their OWN product's passport — the backend
                                    // 403s otherwise, so ownershipHistory here carries identity)
                                    // see the current owner's name. Generic/unauthenticated views
                                    // and other users never do.
                                    if (isBrand) {
                                        if (!passport.is_claimed) return "Brand";
                                        const last = ownershipHistory[ownershipHistory.length - 1];
                                        return (last && (last.owner_name || last.owner_email)) || "Individual";
                                    }
                                    if (isOwner) return passport.is_claimed ? "Me" : "Brand";
                                    return passport.is_claimed ? "Individual" : "Brand";
                                })()}
                            />
                            <Row label="Owned since" value={passport.current_owner_since ? formatDate(passport.current_owner_since) : null} />
                            {/* The backend only returns history to authorized viewers — the
                                linked brand (full chain) or a logged-in owner (bounded to their
                                tenure). Combined with the per-load reset above, a non-empty
                                ownershipHistory here always belongs to THIS product and viewer,
                                so unauthenticated viewers and other brands never see it. */}
                            {ownershipHistory.length > 0 && (
                                <Section title="History">
                                    {ownershipHistory.map((e, i) => {
                                        const label = e.transfer_type === 'initial_sale' ? 'Initial sale'
                                            : e.transfer_type?.replace(/_/g, ' ');
                                        const date = e.date ? formatDate(e.date) : null;
                                        // Brand view includes owner identity; user view has date only.
                                        const owner = e.owner_name || e.owner_email;
                                        const value = owner ? (date ? `${owner} · ${date}` : owner) : date;
                                        return <Row key={i} label={label} value={value} />;
                                    })}
                                </Section>
                            )}
                        </Section>
                        {isOwner && (
                            <Section title="Estate Planning">
                            </Section>
                        )}
                        {!isOwner && !isBrand && (
                            <Section title="Inquiries">
                                {passport.brand_contact_email && (
                                    <a
                                        className="passport-contact-btn"
                                        href={`mailto:${passport.brand_contact_email}`}
                                    >
                                        Contact Merchant
                                    </a>
                                )}
                            </Section>
                        )}
                        <Section title="QR Code">
                            <img
                                src={`${API_BASE_URL}/api/passport/${qrCodeId}/qr.png`}
                                alt="Product QR code"
                                style={{ width: 160, height: 160, display: 'block', margin: '8px auto' }}
                            />
                        </Section>
                        {passport.brand_logo_url && !logoFailed && (
                            <div className="passport-brand-footer">
                                <img
                                    className="passport-brand-logo"
                                    src={passport.brand_logo_url}
                                    alt={passport.brand_name}
                                    onError={() => setLogoFailed(true)}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="passport-dots">
                    {[0, 1, 2, 3].map(i => (
                        <button
                            key={i}
                            className={`passport-dot${activeSlide === i ? ' active' : ''}`}
                            onClick={() => scrollToSlide(i)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

const fmtBool = (v) => v === true ? "Yes" : v === false ? "No" : null;

const RowAll = ({ label, value }) => (
    <div className="passport-row">
        <span className="passport-row-label">{label}</span>
        <span className="passport-row-value">
            {value !== null && value !== undefined && value !== "" ? String(value) : ""}
        </span>
    </div>
);

const Section = ({ title, children }) => (
    <div className="passport-section">
        <h3 className="passport-section-title">{title}</h3>
        {children}
    </div>
);

// Only render rows that actually have a value, so the passport stays clean.
const Row = ({ label, value }) => {
    if (value === null || value === undefined || value === "") return null;
    return (
        <div className="passport-row">
            <span className="passport-row-label">{label}</span>
            <span className="passport-row-value">{String(value)}</span>
        </div>
    );
};

export default Passport;
